import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Copy, Users, TrendingUp, Gift, LogOut, Plus, Sparkles, CheckCircle2, Circle, ShieldCheck, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLAVORS = ['Mango Whey', 'Chocolate Whey', 'Vietnamese Coffee Whey', 'Apple Cinnamon Yeast', 'Chickoo Yeast'];

const getToken = () => localStorage.getItem('insider_session_token');
const authHeaders = () => ({ 'Authorization': `Bearer ${getToken()}` });

export default function InsiderDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [flavorSaving, setFlavorSaving] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyType, setVerifyType] = useState('phone');
  const [verifyValue, setVerifyValue] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);

  const submitVerify = () => {
    if (!verifyValue.trim()) return toast.error(`Please enter your ${verifyType}`);
    setVerifyLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/insider/verify-contact`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    xhr.onload = () => {
      setVerifyLoading(false);
      if (xhr.status === 200) {
        toast.success('You\'re in! KBInsider unlocked 🎉');
        setVerifyOpen(false);
        setVerifyValue('');
        fetchDashboard();
      } else if (xhr.status === 403) {
        try { const err = JSON.parse(xhr.responseText); toast.error(err.detail?.message || 'Not enough orders on this contact.', { duration: 8000 }); } catch { toast.error('Not enough orders.'); }
      } else if (xhr.status === 422) {
        try { const err = JSON.parse(xhr.responseText); toast.error(err.detail || 'Invalid input'); } catch { toast.error('Invalid input'); }
      } else {
        toast.error('Verification failed');
      }
    };
    xhr.onerror = () => { setVerifyLoading(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ contact_type: verifyType, value: verifyValue.trim() }));
  };

  useEffect(() => {
    if (!getToken()) { navigate('/kbinsider'); return; }
    fetchDashboard();
  }, []); // eslint-disable-line

  const fetchDashboard = () => {
    setLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/insider/dashboard`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    xhr.onload = () => {
      setLoading(false);
      if (xhr.status === 200) setData(JSON.parse(xhr.responseText));
      else if (xhr.status === 401 || xhr.status === 403) { localStorage.removeItem('insider_session_token'); navigate('/kbinsider'); }
      else toast.error('Failed to load dashboard');
    };
    xhr.onerror = () => { setLoading(false); toast.error('Network error'); };
    xhr.send();
  };

  const copyLink = () => {
    if (!data?.referral_link) return;
    navigator.clipboard.writeText(data.referral_link);
    toast.success('Link copied!');
  };

  const copyCode = () => {
    if (!data?.user?.referral_code) return;
    navigator.clipboard.writeText(data.user.referral_code);
    toast.success('Code copied!');
  };

  const submitReferral = (e) => {
    e.preventDefault();
    if (!addForm.customer_name.trim()) return toast.error('Name is required');
    if (!addForm.customer_email && !addForm.customer_phone) return toast.error('Email or phone is required');
    setSaving(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/insider/referrals/add`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    xhr.onload = () => {
      setSaving(false);
      if (xhr.status === 200) {
        toast.success('Referral added!');
        setAddOpen(false);
        setAddForm({ customer_name: '', customer_email: '', customer_phone: '', notes: '' });
        fetchDashboard();
      } else if (xhr.status === 409) {
        try { const err = JSON.parse(xhr.responseText); toast.error(err.detail?.message || 'Already referred'); } catch { toast.error('Already referred'); }
      } else if (xhr.status === 422) {
        try { const err = JSON.parse(xhr.responseText); toast.error(err.detail || 'Invalid data'); } catch { toast.error('Invalid data'); }
      } else {
        toast.error('Failed to add referral');
      }
    };
    xhr.onerror = () => { setSaving(false); toast.error('Network error'); };
    xhr.send(JSON.stringify(addForm));
  };

  const updateFlavor = (newFlavor) => {
    setFlavorSaving(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/insider/flavor`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    xhr.onload = () => {
      setFlavorSaving(false);
      if (xhr.status === 200) {
        toast.success('Flavour saved');
        fetchDashboard();
      } else toast.error('Failed to save flavour');
    };
    xhr.onerror = () => { setFlavorSaving(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ flavor: newFlavor }));
  };

  const handleLogout = () => {
    localStorage.removeItem('insider_session_token');
    navigate('/kbinsider');
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  const referrals = data.referrals || [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-black/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_7955cc59-fc5f-4c01-aff4-bac39fc4dad8/artifacts/n9akq2y0_KB%20-%20Final%20Logo%20%28Updated%29-01.webp"
              alt="Kilobeaters"
              className="h-8 w-auto"
            />
            <span className="hidden sm:inline text-white/30">|</span>
            <span className="hidden sm:inline font-semibold tracking-widest text-amber-400 text-sm">INSIDER</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-white/60 hidden sm:inline">{data.user.email}</span>
            <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="insider-logout-btn" className="text-white/70 hover:text-white">
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Hey {data.user.name?.split(' ')[0] || 'there'} 👋
        </h1>
        <p className="text-white/60 mb-8">Track your KBInsider referrals and reward progress here.</p>

        {/* Verification gate — blocks everything below until user verifies */}
        {data.user.status !== 'active' && (
          <Card className="bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent border-amber-400/40 p-6 mb-8 text-white" data-testid="verification-gate">
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-8 h-8 text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-xl mb-1">One last step — verify your Kilobeaters account</h3>
                <p className="text-white/70 text-sm mb-4">
                  Enter the <strong>phone number or email</strong> you used to place your Kilobeaters orders.
                  You need at least <strong>3 orders</strong> on that contact to unlock the program.
                </p>
                <Button onClick={() => { setVerifyType('phone'); setVerifyValue(''); setVerifyOpen(true); }} data-testid="open-verify-btn" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Verify now
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Already active — small "edit registered contact" chip */}
        {data.user.status === 'active' && data.user.verified_contact && (
          <div className="flex items-center gap-2 mb-6 text-sm text-white/60" data-testid="verified-contact-chip">
            <ShieldCheck className="w-4 h-4 text-green-400" />
            {data.user.verified_contact.type === 'admin_override' ? (
              <span>Verified via admin override — "<span className="text-white/80 italic">{data.user.verified_contact.value}</span>"</span>
            ) : (
              <>
                <span>Verified via {data.user.verified_contact.type}: <span className="font-mono text-white/80">{data.user.verified_contact.value}</span></span>
                <button
                  onClick={() => { setVerifyType(data.user.verified_contact.type || 'phone'); setVerifyValue(''); setVerifyOpen(true); }}
                  data-testid="edit-verified-contact-btn"
                  className="text-amber-400 hover:text-amber-300 inline-flex items-center gap-1 ml-2"
                >
                  <Pencil className="w-3 h-3" /> Change
                </button>
              </>
            )}
          </div>
        )}

        {/* Reward banner */}
        {data.reward_earned && !data.user.reward_fulfilled && (
          <Card className="bg-gradient-to-r from-amber-500/20 to-amber-600/10 border-amber-400/40 p-6 mb-8 text-white">
            <div className="flex items-start gap-4">
              <Gift className="w-8 h-8 text-amber-400 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-xl">🎉 You've earned your free 1kg bag!</h3>
                <p className="text-white/70 text-sm mt-1">
                  Our team will reach out to confirm your shipping address. Flavour: <strong>{data.user.flavor_preference || 'not selected yet'}</strong>
                </p>
              </div>
            </div>
          </Card>
        )}
        {data.user.reward_fulfilled && (
          <Card className="bg-green-500/10 border-green-500/30 p-6 mb-8 text-white">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
              <div>
                <h3 className="font-bold">Reward delivered ✓</h3>
                <p className="text-white/70 text-sm">Your free 1kg bag of protein has been shipped.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Progress cards — active only */}
        {data.user.status === 'active' && (
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white/5 border-white/10 p-6 text-white" data-testid="progress-referrals-card">
            <div className="flex items-center gap-2 text-white/60 text-sm mb-3">
              <Users className="w-4 h-4" /> Transacting referrals
            </div>
            <div className="text-3xl font-black">
              <span className="text-amber-400">{data.transacting_referrals}</span>
              <span className="text-white/40 text-xl"> / {data.required_referrals}</span>
            </div>
            <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 transition-all" style={{ width: `${data.referrals_progress_pct}%` }}></div>
            </div>
          </Card>

          <Card className="bg-white/5 border-white/10 p-6 text-white" data-testid="progress-revenue-card">
            <div className="flex items-center gap-2 text-white/60 text-sm mb-3">
              <TrendingUp className="w-4 h-4" /> Combined spend
            </div>
            <div className="text-3xl font-black">
              <span className="text-amber-400">₹{Math.round(data.total_revenue).toLocaleString('en-IN')}</span>
              <span className="text-white/40 text-xl"> / ₹{data.reward_threshold.toLocaleString('en-IN')}</span>
            </div>
            <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 transition-all" style={{ width: `${data.revenue_progress_pct}%` }}></div>
            </div>
          </Card>

          <Card className="bg-white/5 border-white/10 p-6 text-white" data-testid="progress-total-card">
            <div className="flex items-center gap-2 text-white/60 text-sm mb-3">
              <Users className="w-4 h-4" /> Total referrals added
            </div>
            <div className="text-3xl font-black text-white">{data.total_referrals}</div>
            <div className="text-xs text-white/40 mt-1">Includes people who haven't ordered yet</div>
          </Card>
        </div>
        )}

        {/* Referral link — active only */}
        {data.user.status === 'active' && (
        <Card className="bg-white/5 border-white/10 p-6 mb-8 text-white" data-testid="referral-link-card">
          <h3 className="font-bold text-lg mb-1">Your share link ({data.discount_percent}% off for them)</h3>
          <p className="text-white/60 text-sm mb-4">Share this — every purchase counts toward your reward.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-black/40 border border-white/10 rounded-md px-4 py-3 font-mono text-sm break-all">{data.referral_link}</div>
            <Button onClick={copyLink} data-testid="copy-link-btn" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
              <Copy className="w-4 h-4 mr-2" /> Copy link
            </Button>
          </div>
          <div className="mt-4 text-xs text-white/50">
            Or share your code: <button onClick={copyCode} className="text-amber-400 font-mono hover:underline" data-testid="copy-code-btn">{data.user.referral_code}</button>
          </div>
        </Card>
        )}

        {/* Add referral + list — active only */}
        {data.user.status === 'active' && (
        <>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Your referrals</h3>
          <Button onClick={() => setAddOpen(true)} data-testid="add-referral-btn" className="bg-amber-500 hover:bg-amber-600 text-black">
            <Plus className="w-4 h-4 mr-1.5" /> Add referral manually
          </Button>
        </div>

        {/* Referrals list */}
        <Card className="bg-white/5 border-white/10 p-0 overflow-hidden text-white">
          {referrals.length === 0 ? (
            <div className="p-8 text-center text-white/50">
              No referrals yet. Share your link or add someone manually 👆
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {referrals.map((r) => (
                <div key={r.referral_id} className="p-4 flex items-start justify-between gap-4" data-testid={`referral-row-${r.referral_id}`}>
                  <div className="flex items-start gap-3 min-w-0">
                    {r.is_transacting ? (
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                    ) : (
                      <Circle className="w-5 h-5 text-white/30 flex-shrink-0 mt-1" />
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{r.customer_name}</div>
                      <div className="text-xs text-white/50 truncate">{r.customer_email || r.customer_phone}</div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {r.is_transacting ? (
                      <>
                        <div className="text-amber-400 font-semibold">₹{Math.round(r.orders_total).toLocaleString('en-IN')}</div>
                        <div className="text-xs text-white/50">{r.orders_count} order{r.orders_count === 1 ? '' : 's'}</div>
                      </>
                    ) : (
                      <Badge variant="outline" className="text-white/50 border-white/20">Pending</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        </>
        )}

        {/* Flavour picker */}
        <Card className="bg-white/5 border-white/10 p-6 mt-8 text-white" data-testid="flavour-card">
          <h3 className="font-bold text-lg mb-1">Reward flavour</h3>
          <p className="text-white/60 text-sm mb-4">Pick the flavour of your free 1kg bag of protein. You can change this any time before we ship.</p>
          <div className="flex flex-wrap gap-2">
            {FLAVORS.map(f => (
              <button
                key={f}
                onClick={() => updateFlavor(f)}
                disabled={flavorSaving}
                data-testid={`flavour-${f.toLowerCase().replace(/\s|&/g, '-')}`}
                className={`px-4 py-2 rounded-full border text-sm transition ${
                  data.user.flavor_preference === f
                    ? 'bg-amber-500 text-black border-amber-500 font-semibold'
                    : 'border-white/20 text-white/70 hover:border-amber-400 hover:text-amber-400'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Add referral dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#141414] text-white border-white/10">
          <DialogHeader>
            <DialogTitle>Add a referral manually</DialogTitle>
            <DialogDescription className="text-white/60">
              For people who prefer if you share their details directly instead of using your link.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReferral} className="space-y-4" data-testid="add-referral-form">
            <div>
              <Label className="text-white/80">Name *</Label>
              <Input data-testid="add-ref-name" value={addForm.customer_name} onChange={e => setAddForm({ ...addForm, customer_name: e.target.value })} required className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/80">Email</Label>
              <Input data-testid="add-ref-email" type="email" value={addForm.customer_email} onChange={e => setAddForm({ ...addForm, customer_email: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/80">Phone</Label>
              <Input data-testid="add-ref-phone" value={addForm.customer_phone} onChange={e => setAddForm({ ...addForm, customer_phone: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/80">Notes</Label>
              <Textarea data-testid="add-ref-notes" value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} className="bg-white/5 border-white/10 text-white" rows={2} />
            </div>
            <p className="text-xs text-white/40">Provide at least email or phone so we can match their orders.</p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAddOpen(false)} className="text-white/70">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="add-ref-submit" className="bg-amber-500 hover:bg-amber-600 text-black">
                {saving ? 'Saving...' : 'Add referral'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Verify contact dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="bg-[#141414] text-white border-white/10">
          <DialogHeader>
            <DialogTitle>Verify your Kilobeaters account</DialogTitle>
            <DialogDescription className="text-white/60">
              Enter the phone or email you used to place your Kilobeaters orders. We'll check for 3+ orders on that contact.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2 p-1 bg-white/5 rounded-lg" data-testid="verify-type-toggle">
              <button
                type="button"
                onClick={() => setVerifyType('phone')}
                data-testid="verify-type-phone"
                className={`flex-1 py-2 rounded-md text-sm font-medium transition ${verifyType === 'phone' ? 'bg-amber-500 text-black' : 'text-white/60'}`}
              >
                Phone number
              </button>
              <button
                type="button"
                onClick={() => setVerifyType('email')}
                data-testid="verify-type-email"
                className={`flex-1 py-2 rounded-md text-sm font-medium transition ${verifyType === 'email' ? 'bg-amber-500 text-black' : 'text-white/60'}`}
              >
                Email
              </button>
            </div>
            <div>
              <Label className="text-white/80">
                {verifyType === 'phone' ? 'Registered phone (10-digit)' : 'Registered email'}
              </Label>
              <Input
                data-testid="verify-value-input"
                type={verifyType === 'email' ? 'email' : 'tel'}
                value={verifyValue}
                onChange={e => setVerifyValue(e.target.value)}
                placeholder={verifyType === 'phone' ? '9876543210' : 'you@email.com'}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <p className="text-xs text-white/40">
              We match by last-10 digits for phone (with or without +91). No SMS or OTP — we just look up your orders.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setVerifyOpen(false)} className="text-white/70">Cancel</Button>
            <Button onClick={submitVerify} disabled={verifyLoading} data-testid="verify-submit-btn" className="bg-amber-500 hover:bg-amber-600 text-black">
              {verifyLoading ? 'Checking...' : 'Verify & unlock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
